import { useMemo, useState } from "react";
import { ExternalLink, ShieldCheck, Info } from "lucide-react";
import { trackEvent, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ReportButton } from "@/components/ReportButton";
import { Link } from "react-router-dom";

const GROUPS = [["subscription", "Included with Subscription"], ["free", "Free & Legally Available"], ["rent", "Rent"], ["buy", "Buy"], ["cinema", "In Cinemas"]];

function ProviderLogo({ name, logo, color }) {
  if (logo) return <img src={logo} alt={`${name} logo`} className="h-11 w-11 rounded-xl object-cover" loading="lazy" />;
  return <span className="flex h-11 w-11 items-center justify-center rounded-xl text-sm font-black text-white" style={{ background: color || "#333" }}>{name.slice(0, 2).toUpperCase()}</span>;
}

export function WhereToWatch({ title, links }) {
  const { settings } = useAuth();
  const [region, setRegion] = useState(settings.default_region || "IN");
  const providers = Object.fromEntries((settings.providers || []).map((p) => [p.name, p]));
  const regions = settings.regions || [{ code: "IN", name: "India" }];
  const inRegion = useMemo(() => links.filter((l) => l.region === region), [links, region]);
  const regionName = regions.find((r) => r.code === region)?.name || region;

  return (
    <section id="where-to-watch" data-testid="where-to-watch-section" className="scroll-mt-24 rounded-3xl border border-amber-400/20 bg-gradient-to-br from-amber-400/[0.06] via-[#0F0F16] to-purple-900/10 p-5 sm:p-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Legal streaming guide</p>
          <h2 className="font-display text-4xl sm:text-5xl">Where Can You Watch It?</h2>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-400">Region
          <select data-testid="region-select" value={region} onChange={(e) => setRegion(e.target.value)} className="rounded-full border border-white/10 bg-[#12121A] px-3 py-2 text-sm text-white outline-none focus:border-amber-400">
            {regions.map((r) => <option key={r.code} value={r.code}>{r.name}</option>)}
          </select>
        </label>
      </div>
      {inRegion.length === 0 ? (
        <div data-testid="availability-not-confirmed" className="mt-6 flex items-start gap-3 rounded-2xl border border-dashed border-white/10 p-5 text-sm text-slate-400">
          <Info className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" />
          <p><span className="font-semibold text-slate-200">Availability not confirmed</span> for {regionName}. We only list services we have verified — we never guess. Check back soon or try another region.</p>
        </div>
      ) : (
        <div className="mt-6 space-y-6">
          {GROUPS.map(([k, label]) => {
            const items = inRegion.filter((l) => l.type === k);
            if (!items.length) return null;
            return (
              <div key={k} data-testid={`avail-group-${k}`}>
                <p className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-slate-400">{label}</p>
                <div className="grid gap-3 md:grid-cols-2">
                  {items.map((l) => {
                    const p = providers[l.provider] || {};
                    return (
                      <div key={l.id} data-testid={`provider-${l.id}`} className="flex items-center gap-4 rounded-2xl border border-white/5 bg-[#12121A] p-4 hover:border-amber-400/30 transition-colors">
                        <ProviderLogo name={l.provider} logo={l.provider_logo || p.logo} color={p.color} />
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold">{l.provider} {l.is_affiliate && <span className="ml-1 rounded bg-purple-500/20 px-1.5 py-0.5 text-[10px] font-bold uppercase text-purple-300">Affiliate</span>}</p>
                          <p className="text-xs text-slate-500">{label}{l.price ? ` · ${l.price}` : ""}{l.quality ? ` · ${l.quality}` : ""} · {regionName}</p>
                          {l.last_verified_at && <p className="mt-0.5 flex items-center gap-1 text-[11px] text-emerald-400/80"><ShieldCheck className="h-3 w-3" />Verified {fmtDate(l.last_verified_at)}</p>}
                          <ReportButton type="link" id={l.id} linkReport label="Report broken link" className="mt-1" />
                        </div>
                        <a href={l.url} target="_blank" rel={`noopener noreferrer${l.is_affiliate ? " sponsored" : ""}`} data-testid={`visit-platform-${l.id}`}
                          onClick={() => trackEvent("provider_click", { title_id: title.id, link_id: l.id })}
                          className="flex shrink-0 items-center gap-1.5 rounded-full bg-amber-400 px-4 py-2 text-xs font-bold text-black hover:bg-amber-300">
                          Visit <span className="hidden sm:inline">Platform</span><ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {inRegion.some((l) => l.is_affiliate) && <p className="text-xs text-slate-500">{settings.affiliate_disclosure} <Link to="/page/affiliate-disclosure" className="text-amber-400">Learn more</Link></p>}
        </div>
      )}
    </section>
  );
}
